import axios from 'axios'
import { getJobStatus, getBatchJobStatus } from '@/api/caseImages'
import {
  CASE_IMAGE_JOB_STATUS_KEY,
  CASE_IMAGE_JOB_POLL_INTERVAL_MS,
  CASE_IMAGE_JOB_MAX_POLLS,
} from '@messenger/consts'

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

// A single transient blip — a network drop or a 5xx (including the 503
// the backend returns during a brief Auth0/JWKS outage, see Sentry
// MDL-BACKEND-H) — shouldn't fail an upload that is otherwise processing
// fine. Tolerate a few consecutive transient poll failures; a sustained
// outage still fails fast, well inside the overall poll timeout. A 4xx
// (auth / permission / not-found) is terminal and re-throws immediately.
export const MAX_CONSECUTIVE_POLL_ERRORS = 5

export const isTransientPollError = (err: unknown): boolean =>
  axios.isAxiosError(err) && (!err.response || err.response.status >= 500)

export const pollForCompletion = async (
  jobId: string,
): Promise<{ imageUrl: string }> => {
  let consecutiveErrors = 0
  let lastTransientError: unknown = null
  for (let i = 0; i < CASE_IMAGE_JOB_MAX_POLLS; i++) {
    let status: Awaited<ReturnType<typeof getJobStatus>>
    try {
      status = await getJobStatus(jobId)
    } catch (err) {
      if (
        !isTransientPollError(err) ||
        ++consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS
      ) {
        throw err
      }
      lastTransientError = err
      await sleep(CASE_IMAGE_JOB_POLL_INTERVAL_MS)
      continue
    }
    consecutiveErrors = 0
    lastTransientError = null

    if (status.status === CASE_IMAGE_JOB_STATUS_KEY.COMPLETED) {
      const imageUrl =
        status.caseImage?.mioUrl || status.caseImage?.originalUrl || ''
      return { imageUrl }
    }

    if (status.status === CASE_IMAGE_JOB_STATUS_KEY.FAILED) {
      throw new Error(status.errorMessage || 'Image processing failed')
    }

    await sleep(CASE_IMAGE_JOB_POLL_INTERVAL_MS)
  }

  // Exhausted the poll budget. If the final attempts were transient network
  // failures, surface the real cause (better Sentry signal than a generic
  // timeout); otherwise the job genuinely never finished in time.
  if (lastTransientError) throw lastTransientError
  throw new Error('Image processing timed out')
}

export const pollBatchForCompletion = async (
  jobIds: string[],
): Promise<string[]> => {
  let consecutiveErrors = 0
  let lastTransientError: unknown = null
  for (let i = 0; i < CASE_IMAGE_JOB_MAX_POLLS; i++) {
    let result: Awaited<ReturnType<typeof getBatchJobStatus>>
    try {
      result = await getBatchJobStatus(jobIds)
    } catch (err) {
      if (
        !isTransientPollError(err) ||
        ++consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS
      ) {
        throw err
      }
      lastTransientError = err
      await sleep(CASE_IMAGE_JOB_POLL_INTERVAL_MS)
      continue
    }
    consecutiveErrors = 0
    lastTransientError = null
    const { jobs } = result

    const allDone = jobs.every(
      (j) =>
        j.status === CASE_IMAGE_JOB_STATUS_KEY.COMPLETED ||
        j.status === CASE_IMAGE_JOB_STATUS_KEY.FAILED ||
        j.status === 'NOT_FOUND',
    )

    if (allDone) {
      return jobs.map((j) => {
        if (j.status === CASE_IMAGE_JOB_STATUS_KEY.COMPLETED) {
          return j.caseImage?.mioUrl || j.caseImage?.originalUrl || ''
        }
        return ''
      })
    }

    await sleep(CASE_IMAGE_JOB_POLL_INTERVAL_MS)
  }

  if (lastTransientError) throw lastTransientError
  throw new Error('Batch image processing timed out')
}
